import { IAuthorization } from "../dto/auth/Authorization.ts";
import ILedgerRepository, {
  LSAccount,
  LSAccountsList, LSBalance,
  LSList,
  LSTransactionDirection,
  LSTransactionShort,
  LSTransactionStatus,
  LSTransactionType
} from "../interfaces/ILedgerRepository.ts";
import moment from "moment";
import { AxiosResponse } from "axios";
import ILaravelResource from "@/repositories/dto/laravel/ILaravelResource.ts";
import BaseRepository from "@/repositories/implementations/BaseRepository.ts";

export default class LedgerRepository extends BaseRepository implements ILedgerRepository {
  static sessions: Map<string, IAuthorization> = new Map<
    string,
    IAuthorization
  >();

  getAccount(accountId: string): Promise<LSAccount> {
    return Promise.resolve({ id: accountId } as LSAccount);
  }

  getAccounts(/*params: LSGetAccountsParams*/): Promise<LSAccountsList> {
    return Promise.resolve([{}] as LSAccount[]);
  }

  async getTransactions(
    /*params: LSGetTransactionsParams,*/
  ): Promise<LSList<LSTransactionShort>> {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    return {
      list: [
        {
          transactionId: "trx-1",
          type: LSTransactionType.Own,
          createdAt: moment("2024-02-12 12:34:45"),
          signedAt: moment("2024-02-12 12:34:45"),
          executedAt: moment("2024-02-12 12:34:45"),
          fromAccountId: "acc-1",
          toAccountId: "acc-2",
          direction: LSTransactionDirection.Out,
          status: LSTransactionStatus.Created,
          description: "Description",
          amount: {
            amount: 12700,
            currency: "EUR"
          },
          feeAmount: {
            amount: 23,
            currency: "EUR"
          },
          documents: [],
          from: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          },
          to: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          }
        },{
          transactionId: "trx-1",
          type: LSTransactionType.Own,
          createdAt: moment("2024-02-12 12:34:45"),
          signedAt: moment("2024-02-12 12:34:45"),
          executedAt: moment("2024-02-12 12:34:45"),
          fromAccountId: "acc-1",
          toAccountId: "acc-2",
          direction: LSTransactionDirection.Out,
          status: LSTransactionStatus.Created,
          description: "Description",
          amount: {
            amount: 12700,
            currency: "EUR"
          },
          feeAmount: {
            amount: 23,
            currency: "EUR"
          },
          documents: [],
          from: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          },
          to: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          }
        },{
          transactionId: "trx-1",
          type: LSTransactionType.Own,
          createdAt: moment("2024-02-12 12:34:45"),
          signedAt: moment("2024-02-12 12:34:45"),
          executedAt: moment("2024-02-12 12:34:45"),
          fromAccountId: "acc-1",
          toAccountId: "acc-2",
          direction: LSTransactionDirection.Out,
          status: LSTransactionStatus.Created,
          description: "Description",
          amount: {
            amount: 12700,
            currency: "EUR"
          },
          feeAmount: {
            amount: 23,
            currency: "EUR"
          },
          documents: [],
          from: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          },
          to: {
            name: "Janis Ozolins",
            iban: "",
            swift: "",
            countryCode: ""
          }
        }
      ],
      meta: {
        records: 1, currentPage: 1, lastPage: 1,   minEntry: 0, maxEntry: 1
      }
    };
  }

  async getTotalBalance(accountGroup: string, currency: string): Promise<LSBalance> {
    await new Promise((resolve) => setTimeout(resolve, 3000));

    if (accountGroup === 'crypto') {
      return {
        totalAmount: 0,
        onholdAmount: 0,
        availableAmount: 0,
        currencyCode: currency,
      };
    }

    return {
      totalAmount: 2110099,
      onholdAmount: 0,
      availableAmount: 0,
      currencyCode: currency,
    };
  }

  async getTotalBalances(currency: string): Promise<LSBalance[]>
  {
    const response: AxiosResponse<ILaravelResource<LSBalance[]>> | void = await this.post("/ledger/balances/total", { currency });

    if (response) {
      return response.data.data;
    }
    return [];
  }

  async sendCrypto(params: {
    fromAccount: string;
    toAddress: string;
    amount: number;
    currency: string;
    networkFee: number;
    memo?: string;
  }): Promise<{ transactionHash: string; transactionId: string }> {
    const response = await this.post("/ledger/crypto/send", params);
    return (response as any)?.data?.data;
  }

  async buyCrypto(params: {
    cryptocurrency: string;
    amount: number;
    paymentMethod: string;
    fiatAmount: number;
  }): Promise<{ transactionId: string; purchaseId: string }> {
    const response = await this.post("/ledger/crypto/buy", params);
    return (response as any)?.data?.data;
  }

  async exchangeCrypto(params: {
    fromCurrency: string;
    toCurrency: string;
    fromAmount: number;
    toAmount: number;
    slippageTolerance: number;
  }): Promise<{ transactionHash: string; exchangeId: string }> {
    const response = await this.post("/ledger/crypto/exchange", params);
    return (response as any)?.data?.data;
  }

  async sendCash(params: {
    fromAccount: string;
    recipientName: string;
    recipientType: 'bank' | 'email' | 'phone';
    recipientDetails: any;
    amount: number;
    currency: string;
    transferMethod: string;
    memo?: string;
  }): Promise<{ transactionId: string; transferId: string }> {
    const response = await this.post("/ledger/cash/send", params);
    return (response as any)?.data?.data;
  }

  async orderCard(params: {
    cardType: 'virtual' | 'physical' | 'premium';
    cardholderName: string;
    linkedAccount: string;
    design?: string;
    shippingAddress?: any;
    settings: any;
  }): Promise<{ orderNumber: string; cardId: string }> {
    const response = await this.post("/ledger/card/order", params);
    return (response as any)?.data?.data;
  }

  async topUpAccount(params: {
    toAccount: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    paymentDetails: any;
  }): Promise<{ transactionId: string; topUpId: string }> {
    const response = await this.post("/ledger/topup", params);
    return (response as any)?.data?.data;
  }

  async getTransactionHistory(params: {
    accountId?: string;
    startDate?: Date;
    endDate?: Date;
    transactionType?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<LSList<LSTransactionShort>> {
    const response = await this.post("/ledger/transactions/history", params);
    return (response as any)?.data?.data;
  }
}


